import {
  EmailVerificationNotificationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyEmailRequest,
  VerifyEmailResponse
} from './types'
import { API, API_BASE } from '@/api/endpoints'

export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
}

/**
 * register mutation
 */
export const registerMutation = async (
  _url: string,
  { arg }: { arg: RegisterRequest }
): Promise<RegisterResponse> => {
  const res = await fetch(`${API_BASE}${API.REGISTER}`, {
    method: 'POST',
    headers: { ...DEFAULT_HEADERS },
    body: JSON.stringify(arg)
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const error = (data as { message?: string }) || { message: 'Registration failed' }
    throw new Error(error.message || 'Registration failed')
  }

  const message = (data as { message?: string })?.message || 'register.success'

  // Extract token from Authorization header, with fallback to common payload shapes.
  const authHeader = res.headers.get('Authorization')
  const headerToken = authHeader ? authHeader.replace(/^Bearer\s+/i, '') : undefined
  const payload = (data && typeof data === 'object' ? data : null) as Record<string, any> | null
  const bodyToken =
    payload?.token || payload?.access_token || payload?.data?.token || payload?.data?.access_token
  const token = headerToken || bodyToken

  return { message, token }
}

/**
 * Upload profile license document
 */
export const uploadProfileLicense = async (
  token: string,
  file: File
): Promise<{ message: string; path?: string }> => {
  const formData = new FormData()
  formData.append('file', file)

  const res = await fetch(`${API_BASE}/profile/license`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: formData
  })

  const data = (await res.json().catch(() => null)) as { message?: string; path?: string } | null

  if (!res.ok) {
    const error = data || { message: 'License upload failed' }
    throw new Error(error.message || 'License upload failed')
  }

  return {
    message: data?.message || 'License uploaded successfully',
    path: data?.path
  }
}

/**
 * Upload profile ID document
 */
export const uploadProfileIdDocument = async (
  token: string,
  file: File
): Promise<{ message: string; path?: string }> => {
  const formData = new FormData()
  formData.append('file', file)

  const res = await fetch(`${API_BASE}/profile/id-document`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: formData
  })

  const data = (await res.json().catch(() => null)) as { message?: string; path?: string } | null

  if (!res.ok) {
    const error = data || { message: 'ID document upload failed' }
    throw new Error(error.message || 'ID document upload failed')
  }

  return {
    message: data?.message || 'ID document uploaded successfully',
    path: data?.path
  }
}

/**
 * login mutation
 */
export const loginMutation = async (
  _url: string,
  { arg }: { arg: LoginRequest }
): Promise<LoginResponse> => {
  const res = await fetch(`${API_BASE}${API.LOGIN}`, {
    method: 'POST',
    headers: { ...DEFAULT_HEADERS },
    body: JSON.stringify(arg)
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Login failed' }))
    throw new Error(error.message || 'Login failed')
  }

  const authHeader = res.headers.get('Authorization')
  if (!authHeader) {
    throw new Error('No authorization token received')
  }

  const token = authHeader.replace(/^Bearer\s+/i, '')

  return { token }
}

/**
 * logout mutation
 */
export const logoutMutation = async (
  _url: string,
  { arg }: { arg: { token: string } }
): Promise<void> => {
  const res = await fetch(`${API_BASE}${API.LOGOUT}`, {
    method: 'POST',
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${arg.token}`
    }
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Logout failed' }))
    throw new Error(error.message || 'Logout failed')
  }
}

/**
 * refresh token mutation
 */
export const refreshTokenMutation = async (
  _url: string,
  { arg }: { arg: { token: string } }
): Promise<LoginResponse> => {
  const res = await fetch(`${API_BASE}${API.REFRESH_TOKEN}`, {
    method: 'POST',
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${arg.token}`
    }
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: 'Token refresh failed' }))
    throw new Error(error.message || 'Token refresh failed')
  }

  const authHeader = res.headers.get('Authorization')
  if (!authHeader) {
    throw new Error('No authorization token received')
  }

  const token = authHeader.replace(/^Bearer\s+/i, '')

  return { token }
}

/**
 * forgot password mutation
 */
export const forgotPasswordMutation = async (
  _url: string,
  { arg }: { arg: ForgotPasswordRequest }
): Promise<ForgotPasswordResponse> => {
  const res = await fetch(`${API_BASE}${API.FORGOT_PASSWORD}`, {
    method: 'POST',
    headers: { ...DEFAULT_HEADERS },
    body: JSON.stringify(arg)
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const error = (data as { message?: string }) || { message: 'Request failed' }
    throw new Error(error.message || 'Request failed')
  }

  const message = (data as { message?: string })?.message || 'forgot-password.success'

  return { message }
}

/**
 * reset password mutation
 */
export const resetPasswordMutation = async (
  _url: string,
  { arg }: { arg: ResetPasswordRequest }
): Promise<ResetPasswordResponse> => {
  const res = await fetch(`${API_BASE}${API.RESET_PASSWORD}`, {
    method: 'POST',
    headers: { ...DEFAULT_HEADERS },
    body: JSON.stringify(arg)
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const error = (data as { message?: string }) || { message: 'Request failed' }
    throw new Error(error.message || 'Request failed')
  }

  const message = (data as { message?: string })?.message || 'Password reset successfully'

  return { message }
}

/**
 * email verification notification mutation
 */
export const emailVerificationNotificationMutation = async (
  _url: string,
  { arg }: { arg: { token: string } }
): Promise<EmailVerificationNotificationResponse> => {
  const res = await fetch(`${API_BASE}/email/verification-notification`, {
    method: 'POST',
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${arg.token}`
    }
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const error = (data as { message?: string }) || { message: 'Request failed' }
    throw new Error(error.message || 'Request failed')
  }

  const message = (data as { message?: string })?.message || 'Verification email sent'

  return { message }
}

/**
 * verify email mutation
 */
export const verifyEmailMutation = async (
  _url: string,
  { arg }: { arg: VerifyEmailRequest }
): Promise<VerifyEmailResponse> => {
  const res = await fetch(`${API_BASE}${API.EMAIL_VERIFY}`, {
    method: 'POST',
    headers: { ...DEFAULT_HEADERS },
    body: JSON.stringify(arg)
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const error = (data as { message?: string }) || { message: 'Verification failed' }
    throw new Error(error.message || 'Verification failed')
  }

  const message = (data as { message?: string })?.message || 'Email verified successfully'

  return { message }
}

/**
 * Change password mutation (authenticated user)
 */
export type ChangePasswordRequest = {
  current: string
  password: string
  password_confirmation: string
}

export const changePasswordMutation = async (
  token: string,
  data: ChangePasswordRequest
): Promise<void> => {
  const res = await fetch(`${API_BASE}/profile/password`, {
    method: 'PUT',
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(data)
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => null)

    // Handle validation errors
    if (res.status === 422 && errorData?.errors) {
      const errors = errorData.errors as Record<string, string[]>
      const firstErrorField = Object.keys(errors)[0]
      const firstError = errors[firstErrorField]?.[0]
      throw new Error(firstError || 'Errore di validazione')
    }

    const error = (errorData as { message?: string }) || { message: 'Password change failed' }
    throw new Error(error.message || 'Password change failed')
  }
}
